<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiChip from '@/components/pos-v2/ui/PosUiChip.vue';
  import { paymentChips } from '@/domain/store-setup/orders-setup';
  import type {
    IOrdersForm,
    IPaymentChip,
  } from '@/domain/store-setup/orders-setup';
  import {
    isPaymentChipOn,
    togglePayment,
  } from '@/domain/store-setup/orders-step';
  import type { TOrdersFormPatch } from '@/domain/store-setup/orders-step';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * «¿Cómo le pagan?» (plan §4.8 item 3): one multi-select chip per payment
   * method (cash chosen by default). Mercado Pago appears only where it can be
   * connected and while orders arrive in the panel; choosing it only shows a
   * note (the dashboard checklist does the connecting).
   */
  const props = withDefaults(
    defineProps<{
      value: IOrdersForm;
      country: string;
      disabled?: boolean;
    }>(),
    { disabled: false }
  );
  const emit = defineEmits<{
    (e: 'patch', patch: TOrdersFormPatch): void;
  }>();

  const titleId = nextDomId('orders-pay');
  const chips = computed(() => paymentChips(props.country, props.value.mode));
  const showMpNote = computed(
    () =>
      props.value.mercadoPago &&
      chips.value.some((chip) => chip.id === 'mercadoPago')
  );

  function isOn(chip: IPaymentChip): boolean {
    return isPaymentChipOn(props.value, chip.id);
  }

  function toggle(chip: IPaymentChip) {
    if (!props.disabled) emit('patch', togglePayment(props.value, chip.id));
  }
</script>

<template>
  <section class="v2-setup-payment-chips">
    <header class="card-head">
      <h2 :id="titleId" class="card-title">
        {{ $t('storeSetup.orders.pay') }}
      </h2>
      <p class="card-text">{{ $t('storeSetup.orders.payText') }}</p>
    </header>
    <div class="chip-list" role="group" :aria-labelledby="titleId">
      <PosUiChip
        v-for="chip in chips"
        :key="chip.id"
        class="pay-chip"
        :active="isOn(chip)"
        :disabled="disabled"
        @click="toggle(chip)"
        >{{ $t(chip.labelKey) }}</PosUiChip
      >
    </div>
    <p v-if="showMpNote" class="mp-note">
      {{ $t('storeSetup.orders.mpNote') }}
    </p>
  </section>
</template>

<style scoped lang="scss">
  .v2-setup-payment-chips {
    @apply tw-flex tw-flex-col tw-gap-4 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-4 md:tw-p-5;
    .card-head {
      @apply tw-flex tw-flex-col tw-gap-0.5;
    }
    .card-title {
      @apply tw-m-0 tw-text-base tw-font-semibold tw-text-neutral-900;
    }
    .card-text {
      @apply tw-m-0 tw-text-sm tw-text-neutral-500;
    }
    .chip-list {
      @apply tw-flex tw-flex-wrap tw-gap-2;
      // A 44 px touch target even for a short label («QR»).
      .pay-chip {
        @apply tw-min-w-11;
      }
    }
    .mp-note {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
  }
</style>
